/** Pure marketplace contracts, listing-template helpers, and reconciliation helpers. */

import type { CardPresentation } from './card-presentation';
import type { EbayCategoryPolicy, EbayCategoryPolicyBinding } from './ebay-category-policy';
import type { CardIdentity, ChecklistSourceSelection } from './types';
import type { EbayItemSpecifics, EbaySellerHubSinglesCategory } from './ebay-seller-hub';

export const EBAY_TITLE_MAX_LENGTH = 80;
export const MARKETPLACE_TITLE_DISCRIMINATOR_LENGTH = 2;
export const MARKETPLACE_TITLE_DISCRIMINATOR_SEPARATOR = ' ';
export const EBAY_LISTING_BASE_TITLE_MAX_LENGTH = /* @__PURE__ */ (() =>
  EBAY_TITLE_MAX_LENGTH -
  MARKETPLACE_TITLE_DISCRIMINATOR_SEPARATOR.length -
  MARKETPLACE_TITLE_DISCRIMINATOR_LENGTH)();
export const MARKETPLACE_DUPLICATE_LISTING_DEFAULT_LIMIT = 3;
export const LISTING_TEMPLATE_TARGET_LENGTH_DEFAULT = 70;
export const LISTING_TEMPLATE_NAME_MAX_LENGTH = 120;
export const LISTING_TEMPLATE_DESCRIPTION_MAX_LENGTH = 1000;
export const LISTING_TEMPLATE_DESCRIPTION_PROMPT_MAX_LENGTH = 2000;
export const LISTING_TEMPLATE_TEXT_MAX_LENGTH = 80;
export const LISTING_TEMPLATE_MAX_BLOCKS = 30;
export const CHECKLIST_TEMPLATE_ASSIGNMENT_MAX_TEMPLATES = 50;
export const LISTING_DESCRIPTION_MAX_LENGTH = 1000;
export const LISTING_DESCRIPTION_MAX_OUTPUT_TOKENS = 400;
export const LISTING_TEMPLATE_DESCRIPTION_MAX_BLOCKS = 60;
export const LISTING_TEMPLATE_DESCRIPTION_TEXT_MAX_LENGTH = LISTING_DESCRIPTION_MAX_LENGTH;

export const LISTING_TEMPLATE_ATTRIBUTES = [
  'year',
  'brand',
  'product',
  'subset',
  'cardNumber',
  'name',
  'team',
  'parallel',
  'rookie',
  'serialNumber',
  'printRun',
  'grader',
  'grade',
  'certNumber',
  'sku',
  'shortCode',
] as const;

export type ListingTemplateAttribute = (typeof LISTING_TEMPLATE_ATTRIBUTES)[number];
export type ListingTemplateStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
export type ListingTemplateSeparator = '' | ' ' | ' - ' | ' / ' | ' | ';

interface ListingTemplateBlockBase {
  blockId: string;
  separatorBefore: ListingTemplateSeparator;
}

export interface ListingTemplateAttributeBlock extends ListingTemplateBlockBase {
  kind: 'ATTRIBUTE';
  attribute: ListingTemplateAttribute;
}

export interface ListingTemplateTextBlock extends ListingTemplateBlockBase {
  kind: 'TEXT';
  text: string;
}

export type ListingTemplateBlock = ListingTemplateAttributeBlock | ListingTemplateTextBlock;

export interface ListingTemplateDescriptionAttributeBlock extends ListingTemplateBlockBase {
  kind: 'ATTRIBUTE';
  attribute: ListingTemplateAttribute;
}

export interface ListingTemplateDescriptionTextBlock extends ListingTemplateBlockBase {
  kind: 'TEXT';
  text: string;
}

export interface ListingTemplateDescriptionLineBreakBlock {
  blockId: string;
  kind: 'LINE_BREAK';
  lines: 1 | 2;
}

export type ListingTemplateDescriptionBlock =
  | ListingTemplateDescriptionAttributeBlock
  | ListingTemplateDescriptionTextBlock
  | ListingTemplateDescriptionLineBreakBlock;

export type ListingTemplateSampleValues = Partial<Record<ListingTemplateAttribute, string>>;

export interface MarketplaceListingTemplate {
  templateId: string;
  provider: 'EBAY_US';
  name: string;
  description: string;
  defaultDescription: string;
  descriptionPrompt: string;
  status: ListingTemplateStatus;
  titleTargetLength: number;
  titleBlocks: ListingTemplateBlock[];
  descriptionBlocks: ListingTemplateDescriptionBlock[];
  version: number;
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface SaveMarketplaceListingTemplateRequest {
  name: string;
  description: string;
  defaultDescription?: string;
  descriptionPrompt: string;
  status: ListingTemplateStatus;
  titleTargetLength: number;
  titleBlocks: ListingTemplateBlock[];
  descriptionBlocks?: ListingTemplateDescriptionBlock[];
  expectedVersion?: number;
  /** Scoped proof that this authoring request originated from card review. */
  reviewContext?: {
    checklistId: string;
    sourceTemplateId: string;
    mode: 'EDIT' | 'CLONE';
  };
}

export interface ListMarketplaceListingTemplatesResponse {
  templates: MarketplaceListingTemplate[];
  defaultTemplateId?: string;
  /** Account default for trading-card-game products without a checklist assignment. */
  defaultCcgTemplateId?: string;
}

export interface MarketplaceListingTemplateResponse {
  template: MarketplaceListingTemplate;
}

export interface SetMarketplaceDefaultListingTemplateRequest {
  operation: 'SET_DEFAULT_LISTING_TEMPLATE';
  templateId: string | null;
  /** 'CCG' sets the trading-card-game default; omitted sets the account (sports) default. */
  scope?: 'CCG';
}

export interface SetMarketplaceDefaultListingTemplateResponse {
  defaultTemplateId?: string;
  defaultCcgTemplateId?: string;
}

export type MarketplaceListingTemplateSource = 'ASSIGNMENT' | 'DEFAULT' | 'NONE';

export interface MarketplaceChecklistTemplateAssignment {
  checklistId: string;
  templateIds: string[];
  revision: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SaveMarketplaceChecklistTemplateAssignmentRequest {
  templateIds: string[];
  expectedRevision: number;
}

export interface MarketplaceChecklistTemplateAssignmentResponse {
  assignment: MarketplaceChecklistTemplateAssignment;
  templates?: MarketplaceListingTemplate[];
  templateSource?: MarketplaceListingTemplateSource;
  defaultTemplateId?: string;
}

export interface MarketplaceListingReviewContextResponse {
  checklistId: string;
  assignedTemplateCount: number;
  templates: MarketplaceListingTemplate[];
  templateSource: MarketplaceListingTemplateSource;
  defaultTemplateId?: string;
}

export interface GenerateMarketplaceListingDescriptionRequest {
  operation: 'GENERATE_DESCRIPTION';
  cardId: string;
  identitySource?: MarketplaceListingIdentitySource;
  checklistId?: string;
  templateId: string;
  title: string;
  identity: CardIdentity;
  sku?: string;
  shortCode?: string;
}

export type MarketplaceListingIdentitySource = 'CHECKLIST' | 'MANUAL_OVERRIDE';

export interface GenerateMarketplaceListingDescriptionResponse {
  description: string;
  model: string;
  generationSource: 'BEDROCK' | 'DETERMINISTIC_FALLBACK';
  templateId: string;
  templateVersion: number;
}

export type MarketplaceComparableState = 'NON_LISTED' | 'LISTED' | 'SOLD';
export type MarketplaceComparableProviderStatus = 'ACTIVE' | 'MISSING' | 'ENDED' | 'SOLD';
export type MarketplaceListingReadinessStatus = 'READY_TO_LIST' | 'APPROVED_FOR_EBAY';

export function marketplaceDuplicateGroupKey(
  checklistComparablePk: string | undefined,
  cardPresentation: CardPresentation | undefined,
): string | undefined {
  const comparable = checklistComparablePk?.trim();
  if (comparable === undefined || comparable === '' || cardPresentation === undefined) {
    return undefined;
  }
  const presentation =
    cardPresentation.kind === 'RAW'
      ? `RAW#${cardPresentation.condition}`
      : `GRADED#${cardPresentation.grader}#${cardPresentation.grade}`;
  return `${comparable}#PRESENTATION#${presentation}`;
}

/** A stored title code: two letters, or the earlier letter plus digit still on older records. */
export function isMarketplaceTitleDiscriminator(value: unknown): value is string {
  return typeof value === 'string' && /^(?:[A-Z][0-9]|[A-Z]{2})$/.test(value);
}

/**
 * Title codes for new eBay listings, in the order they are given out (owner, 2026-10-30): a copy takes the first code
 * not already used by another approved or live copy of the same item, so with at most three at a time the codes are
 * AA, BB and CC. Doubled letters only: no digits ("X7", "7x" read as a quantity, which eBay refuses on a single-item
 * listing), no QQ or XX, and no pair that reads as a grade, condition or card attribute.
 */
export const MARKETPLACE_TITLE_DISCRIMINATOR_CODES: readonly string[] = [
  'AA',
  'BB',
  'CC',
  'DD',
  'EE',
  'FF',
  'GG',
  'HH',
  'II',
  'JJ',
  'KK',
  'LL',
  'MM',
  'NN',
  'OO',
  'PP',
  'RR',
  'SS',
  'TT',
  'UU',
  'VV',
  'WW',
  'YY',
  'ZZ',
];

/** A title code TCVS may put on a new eBay listing; older codes are replaced when the card is approved. */
export function isPublishableTitleDiscriminator(value: unknown): value is string {
  return typeof value === 'string' && MARKETPLACE_TITLE_DISCRIMINATOR_CODES.includes(value);
}

/** An older code eBay reads as a quantity ("X7", "Q7"); such a title must not be exported. */
export function titleDiscriminatorReadsAsQuantity(value: unknown): boolean {
  return typeof value === 'string' && /^[QX][0-9]$/.test(value);
}

export function marketplaceListingTitle(
  baseTitle: string,
  discriminator: string | undefined,
): string {
  const title = baseTitle.trim();
  return isMarketplaceTitleDiscriminator(discriminator)
    ? `${title}${MARKETPLACE_TITLE_DISCRIMINATOR_SEPARATOR}${discriminator}`
    : title;
}

export interface MarketplaceComparableItem {
  /** Stable render key across inventory-backed and external-only evidence. */
  comparableId: string;
  cardId?: string;
  marketplaceListingId?: string;
  externalOnly?: true;
  shortCode?: string;
  batchId?: string;
  batchRelation?: 'CURRENT_BATCH' | 'OTHER_BATCH';
  identificationComplete: true;
  state: MarketplaceComparableState;
  providerStatus?: MarketplaceComparableProviderStatus;
  identity: CardIdentity;
  cardPresentation?: CardPresentation;
  amountMinor?: number;
  listedAmountMinor?: number;
  soldAmountMinor?: number;
  currency: 'USD';
  priceSource?: 'TCVS_REVIEWED' | 'EBAY_OBSERVED' | 'EBAY_SOLD';
  soldAt?: string;
  soldAtSource?: 'EBAY_FULFILLMENT_ORDER' | 'EBAY_TRADING_END_TIME' | 'TCVS_OBSERVATION';
  listedDays?: number;
  title?: string;
  imageUrl?: string;
}

export interface MarketplaceCardComparablesResponse {
  number: string;
  parallel: string;
  items: MarketplaceComparableItem[];
}

export type EbayActiveComparableMatchQuality = 'EXACT' | 'LIKELY';

export interface EbayActiveComparableItem {
  itemId: string;
  title: string;
  itemUrl: string;
  imageUrl?: string;
  amountMinor: number;
  shippingMinor?: number;
  totalMinor?: number;
  currency: 'USD';
  condition?: string;
  sellerUsername?: string;
  buyingOptions: string[];
  matchQuality: EbayActiveComparableMatchQuality;
  matchReasons: string[];
}

export interface EbayActiveComparableSummary {
  count: number;
  lowAmountMinor?: number;
  medianAmountMinor?: number;
  highAmountMinor?: number;
}

export interface EbayActiveComparablesResponse {
  status: 'AVAILABLE' | 'UNAVAILABLE';
  query: string;
  asOf: string;
  cached: boolean;
  expiresAt?: string;
  unavailableReason?: 'NOT_CONFIGURED' | 'ACCESS_DENIED' | 'PROVIDER_UNAVAILABLE';
  summary: EbayActiveComparableSummary;
  items: EbayActiveComparableItem[];
}

export interface ExternalListingChecklistOption {
  optionId: string;
  label: string;
  identity: CardIdentity;
  checklistSourceSelection: ChecklistSourceSelection;
}

export interface ResolveExternalListingChecklistRequest {
  operation: 'RESOLVE_EXTERNAL_LISTING_CHECKLIST';
  ebayImportId: string;
  ebayRowNumber: number;
  checklistId: string;
  cardNumber: string;
}

export interface ResolveExternalListingChecklistResponse {
  itemNumber: string;
  title?: string;
  options: ExternalListingChecklistOption[];
}

export type ExternalListingChecklistSuggestionTier =
  | 'HIGH_CONFIDENCE'
  | 'REVIEWABLE'
  | 'AMBIGUOUS'
  | 'NO_CANDIDATE';

export interface SuggestExternalListingChecklistRequest {
  operation: 'SUGGEST_EXTERNAL_LISTING_CHECKLIST';
  ebayImportId: string;
  ebayRowNumber: number;
}

export interface SuggestExternalListingChecklistResponse {
  itemNumber: string;
  title?: string;
  tier: ExternalListingChecklistSuggestionTier;
  reason: string;
  checklistId?: string;
  cardNumber?: string;
  options: ExternalListingChecklistOption[];
  recommendedOptionId?: string;
}

export interface ConfirmExternalListingChecklistRequest {
  operation: 'CONFIRM_EXTERNAL_LISTING_CHECKLIST';
  /** Optional historical comparison context. New eBay links do not require CDP. */
  cdpImportId?: string;
  ebayImportId: string;
  ebayRowNumber: number;
  reviewKey: string;
  checklistId: string;
  cardNumber: string;
  optionId: string;
  note?: string;
}

export interface ConfirmExternalListingChecklistResponse {
  review: MarketplaceReconciliationReview;
  marketplaceListingId: string;
  comparableKey: string;
}

/** Explicit browser review intent; server reacquires policy and authenticated provenance. */
export interface MarketplaceCcgReviewRequest {
  schemaVersion: 1;
  policyReviewIdentity: string;
  specifics: Partial<
    Record<'Finish' | 'Rarity', { origin: 'MANUAL_REVIEW' | 'IMPORTED_RESEARCH'; value: string }>
  >;
}
export interface MarketplaceCcgLimits {
  ccgExportCards: 19;
}
export interface MarketplaceCcgPolicyProjection {
  reviewIdentity: string;
  binding: EbayCategoryPolicyBinding;
}
export interface GetEbayCategoryPolicyRequest {
  operation: 'GET_EBAY_CATEGORY_POLICY';
  category: 'CCG_SINGLES';
}
export interface GetEbayCategoryPolicyResponse {
  category: 'CCG_SINGLES';
  policy: EbayCategoryPolicy;
  limits: MarketplaceCcgLimits;
}
export type MarketplaceCcgAction =
  | 'REVIEW_AND_SAVE_AGAIN'
  | 'RELOAD_POLICY'
  | 'TRY_AGAIN'
  | 'CHOOSE_FEWER_CARDS'
  | 'CHECK_STATUS';
export interface MarketplaceCcgWorkflowFailure {
  error: string;
  message: string;
  action: MarketplaceCcgAction;
  limits: MarketplaceCcgLimits;
}

export interface SaveMarketplaceListingReadinessRequest {
  ccgReview?: MarketplaceCcgReviewRequest;
  title: string;
  description?: string;
  templateId: string;
  identitySource?: MarketplaceListingIdentitySource;
  checklistId?: string;
  amountMinor: number;
  currency: 'USD';
  cardPresentation: CardPresentation;
  itemSpecifics?: EbayItemSpecifics;
  itemSpecificsCategory?: EbaySellerHubSinglesCategory;
}

export interface MarketplaceListingCopyRecord {
  cardId: string;
  marketplace: 'EBAY_US';
  title: string;
  description?: string;
  templateId: string;
  identitySource: MarketplaceListingIdentitySource;
  checklistId?: string;
  identity: CardIdentity;
  cardPresentation?: CardPresentation;
  itemSpecifics?: EbayItemSpecifics;
  itemSpecificsCategory?: EbaySellerHubSinglesCategory;
  status?: MarketplaceListingReadinessStatus;
  updatedAt: string;
}

export interface MarketplaceListingCopyResponse {
  limits?: MarketplaceCcgLimits;
  item: MarketplaceListingCopyRecord | null;
}

export type MarketplaceListingStorageStatus = 'PLACED' | 'UNPLACED' | 'NOT_STORED' | 'UNAVAILABLE';

export interface MarketplaceListingStorageLocation {
  status: MarketplaceListingStorageStatus;
  shelf?: { entityId: string; shelfCode: string };
  box?: { entityId: string };
  row?: { entityId: string; rowCode?: string };
  batch?: { entityId: string };
}

/** Frozen export metadata; user overrides remain separate from resolved defaults. */
export interface MarketplaceListingExportSnapshot {
  schemaVersion: 1;
  category: Exclude<EbaySellerHubSinglesCategory, 'CCG_SINGLES'>;
  itemSpecifics: EbayItemSpecifics;
  capturedAt: string;
  provenance: {
    mode: 'REVIEW' | 'BACKFILL';
    checklistTable?: string;
    checklistId?: string;
    sourceItemId?: string;
    snapshotFingerprint?: string;
  };
}

/** CCG approval evidence only; source research never creates this reviewed snapshot. */
export interface MarketplaceCcgListingExportSnapshot {
  schemaVersion: 2;
  category: 'CCG_SINGLES';
  itemSpecifics: EbayItemSpecifics;
  capturedAt: string;
  policyBinding: EbayCategoryPolicyBinding;
  /** Semantic identity excludes refresh timestamps and raw response formatting. */
  policyReviewIdentity: string;
  provenance: {
    mode: 'REVIEW';
    reviewedAt: string;
    reviewedBy: string;
    specifics: Partial<
      Record<
        'Finish' | 'Rarity',
        {
          origin: 'MANUAL_REVIEW' | 'IMPORTED_RESEARCH';
          reviewedValue: string;
          reviewedAt: string;
          reviewedBy: string;
        }
      >
    >;
  };
}

export interface MarketplaceListingReadinessRecord {
  ccgPolicy?: MarketplaceCcgPolicyProjection;
  limits?: MarketplaceCcgLimits;
  cardId: string;
  marketplace: 'EBAY_US';
  status: MarketplaceListingReadinessStatus;
  title: string;
  /** Durable per-physical-card suffix appended only to provider-facing copy. */
  titleDiscriminator?: string;
  marketplaceTitle?: string;
  duplicateGroupKey?: string;
  duplicateGroupLimit: number;
  /** Distinct cards already approved or observed active in this exact duplicate group. */
  duplicateGroupCommittedCount?: number;
  description?: string;
  templateId: string;
  identitySource: MarketplaceListingIdentitySource;
  checklistId?: string;
  amountMinor: number;
  currency: 'USD';
  identity: CardIdentity;
  cardPresentation?: CardPresentation;
  exportSnapshot?: MarketplaceListingExportSnapshot | MarketplaceCcgListingExportSnapshot;
  itemSpecifics?: EbayItemSpecifics;
  itemSpecificsCategory?: EbaySellerHubSinglesCategory;
  queuedAt: string;
  queuedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  exportFingerprint?: string;
  exportedAt?: string;
  exportedBy?: string;
  updatedAt: string;
  imageUrl?: string;
  storageLocation?: MarketplaceListingStorageLocation;
}

export interface MarketplaceListingReadinessResponse {
  limits?: MarketplaceCcgLimits;
  item: MarketplaceListingReadinessRecord;
}

export interface ListMarketplaceListingReadinessResponse {
  items: MarketplaceListingReadinessRecord[];
  nextToken?: string;
}

export interface ListingTemplateValidation {
  valid: boolean;
  errors: string[];
}

export function listingTemplateAttributeLabel(attribute: ListingTemplateAttribute): string {
  const labels: Record<ListingTemplateAttribute, string> = {
    year: 'Year',
    brand: 'Brand',
    product: 'Product',
    subset: 'Subset',
    cardNumber: 'Card number',
    name: 'Player / card name',
    team: 'Team',
    parallel: 'Parallel',
    rookie: 'Rookie',
    serialNumber: 'Serial number',
    printRun: 'Print run',
    grader: 'Grader',
    grade: 'Grade',
    certNumber: 'Certification number',
    sku: 'SKU',
    shortCode: 'Card short ID',
  };
  return labels[attribute];
}

function rookieText(value: string | undefined): boolean {
  return value !== undefined && /(?:^|[\s,;/|])(?:rc|rookie)(?:$|[\s,;/|])/i.test(value.trim());
}

/** Compatibility-aware rookie detection until every imported source carries a boolean flag. */
export function listingIdentityIsRookie(identity: Partial<CardIdentity>): boolean {
  return (
    identity.rookie === true ||
    rookieText(identity.attributes) ||
    /\brookies?\b/i.test(identity.subset?.trim() ?? '')
  );
}

export function listingTemplateValuesForCard(
  identity: Partial<CardIdentity>,
  card: {
    sku?: string;
    shortCode?: string;
    cardPresentation?: CardPresentation;
  } = {},
): ListingTemplateSampleValues {
  const printRun =
    identity.serialTo === undefined
      ? undefined
      : identity.serialTo === 1
        ? '1/1'
        : `/${identity.serialTo}`;
  const observedSerial =
    identity.serialNumber === undefined || identity.serialTo === undefined
      ? undefined
      : `${identity.serialNumber}/${identity.serialTo}`;
  return {
    ...(identity.year?.trim() ? { year: identity.year.trim() } : {}),
    ...(identity.brand?.trim() ? { brand: identity.brand.trim() } : {}),
    ...(identity.set?.trim() ? { product: identity.set.trim() } : {}),
    ...(identity.subset?.trim() ? { subset: identity.subset.trim() } : {}),
    ...(identity.number?.trim() ? { cardNumber: `#${identity.number.trim()}` } : {}),
    ...(identity.name?.trim() ? { name: identity.name.trim() } : {}),
    ...(identity.team?.trim() ? { team: identity.team.trim() } : {}),
    ...(identity.variation?.trim() ? { parallel: identity.variation.trim() } : {}),
    ...(listingIdentityIsRookie(identity) ? { rookie: 'RC' } : {}),
    ...(printRun === undefined ? {} : { serialNumber: observedSerial ?? printRun, printRun }),
    ...(card.sku?.trim() ? { sku: card.sku.trim() } : {}),
    ...(card.shortCode?.trim() ? { shortCode: card.shortCode.trim() } : {}),
    ...(card.cardPresentation?.kind === 'GRADED'
      ? {
          grader: card.cardPresentation.grader,
          grade: String(card.cardPresentation.grade),
          ...(card.cardPresentation.certificationNumber === undefined
            ? {}
            : { certNumber: card.cardPresentation.certificationNumber }),
        }
      : {}),
  };
}

/** Render non-empty parts only, so an absent optional attribute cannot leave punctuation behind. */
export function renderListingTemplateTitle(
  blocks: readonly ListingTemplateBlock[],
  values: ListingTemplateSampleValues,
): string {
  const emitted: Array<{ separator: ListingTemplateSeparator; value: string }> = [];
  for (const block of blocks) {
    const value =
      block.kind === 'TEXT' ? block.text.trim() : (values[block.attribute]?.trim() ?? '');
    if (value !== '') emitted.push({ separator: block.separatorBefore, value });
  }
  return emitted
    .map((part, index) => `${index === 0 ? '' : part.separator}${part.value}`)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Render a multi-line description while preserving explicit line-break blocks. */
export function renderListingTemplateDescription(
  blocks: readonly ListingTemplateDescriptionBlock[],
  values: ListingTemplateSampleValues,
): string {
  let rendered = '';
  for (const block of blocks) {
    if (block.kind === 'LINE_BREAK') {
      if (rendered.trim() === '') continue;
      rendered = `${rendered.replace(/[ \t\n]+$/g, '')}${'\n'.repeat(block.lines)}`;
      continue;
    }
    const value =
      block.kind === 'TEXT' ? block.text.trim() : (values[block.attribute]?.trim() ?? '');
    if (value === '') continue;
    const atLineStart = rendered === '' || rendered.endsWith('\n');
    rendered += `${atLineStart ? '' : block.separatorBefore}${value}`;
  }
  return rendered
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function validateListingTemplate(
  input: SaveMarketplaceListingTemplateRequest,
  sampleValues?: ListingTemplateSampleValues,
): ListingTemplateValidation {
  const errors: string[] = [];
  const name = input.name.trim();
  if (name === '') errors.push('Template name is required.');
  if (name.length > LISTING_TEMPLATE_NAME_MAX_LENGTH)
    errors.push(`Template name must be ${LISTING_TEMPLATE_NAME_MAX_LENGTH} characters or fewer.`);
  if (input.description.length > LISTING_TEMPLATE_DESCRIPTION_MAX_LENGTH)
    errors.push(
      `Template description must be ${LISTING_TEMPLATE_DESCRIPTION_MAX_LENGTH} characters or fewer.`,
    );
  if ((input.defaultDescription ?? '').length > LISTING_DESCRIPTION_MAX_LENGTH)
    errors.push(
      `Default listing description must be ${LISTING_DESCRIPTION_MAX_LENGTH} characters or fewer.`,
    );
  if (input.descriptionPrompt.length > LISTING_TEMPLATE_DESCRIPTION_PROMPT_MAX_LENGTH)
    errors.push(
      `AI description prompt must be ${LISTING_TEMPLATE_DESCRIPTION_PROMPT_MAX_LENGTH} characters or fewer.`,
    );
  if (!['DRAFT', 'ACTIVE', 'ARCHIVED'].includes(input.status))
    errors.push('Template status is invalid.');
  if (
    !Number.isInteger(input.titleTargetLength) ||
    input.titleTargetLength < 1 ||
    input.titleTargetLength > EBAY_LISTING_BASE_TITLE_MAX_LENGTH
  )
    errors.push(`Title target must be between 1 and ${EBAY_LISTING_BASE_TITLE_MAX_LENGTH}.`);
  if (input.titleBlocks.length === 0) errors.push('Add at least one title part.');
  if (input.titleBlocks.length > LISTING_TEMPLATE_MAX_BLOCKS)
    errors.push(`Use no more than ${LISTING_TEMPLATE_MAX_BLOCKS} title parts.`);
  const blockIds = new Set<string>();
  for (const block of input.titleBlocks) {
    if (block.blockId.trim() === '' || blockIds.has(block.blockId))
      errors.push('Every title part must have a unique ID.');
    blockIds.add(block.blockId);
    if (!['', ' ', ' - ', ' / ', ' | '].includes(block.separatorBefore))
      errors.push('A title separator is invalid.');
    if (block.kind === 'ATTRIBUTE') {
      if (!LISTING_TEMPLATE_ATTRIBUTES.includes(block.attribute))
        errors.push('A title attribute is invalid.');
    } else if (block.kind === 'TEXT') {
      if (block.text.trim() === '') errors.push('Free text title parts cannot be empty.');
      if (block.text.length > LISTING_TEMPLATE_TEXT_MAX_LENGTH)
        errors.push(`Free text must be ${LISTING_TEMPLATE_TEXT_MAX_LENGTH} characters or fewer.`);
    } else errors.push('A title part type is invalid.');
  }
  const descriptionBlocks = input.descriptionBlocks ?? [];
  if (descriptionBlocks.length > LISTING_TEMPLATE_DESCRIPTION_MAX_BLOCKS)
    errors.push(`Use no more than ${LISTING_TEMPLATE_DESCRIPTION_MAX_BLOCKS} description parts.`);
  const descriptionBlockIds = new Set<string>();
  for (const block of descriptionBlocks) {
    if (block.blockId.trim() === '' || descriptionBlockIds.has(block.blockId))
      errors.push('Every description part must have a unique ID.');
    descriptionBlockIds.add(block.blockId);
    if (block.kind === 'LINE_BREAK') {
      if (block.lines !== 1 && block.lines !== 2)
        errors.push('A description line break is invalid.');
      continue;
    }
    if (!['', ' ', ' - ', ' / ', ' | '].includes(block.separatorBefore))
      errors.push('A description separator is invalid.');
    if (block.kind === 'ATTRIBUTE') {
      if (!LISTING_TEMPLATE_ATTRIBUTES.includes(block.attribute))
        errors.push('A description attribute is invalid.');
    } else if (block.kind === 'TEXT') {
      if (block.text.trim() === '') errors.push('Free text description parts cannot be empty.');
      if (block.text.length > LISTING_TEMPLATE_DESCRIPTION_TEXT_MAX_LENGTH)
        errors.push(
          `Description free text must be ${LISTING_TEMPLATE_DESCRIPTION_TEXT_MAX_LENGTH} characters or fewer.`,
        );
    } else errors.push('A description part type is invalid.');
  }
  if (
    sampleValues !== undefined &&
    renderListingTemplateTitle(input.titleBlocks, sampleValues).length >
      EBAY_LISTING_BASE_TITLE_MAX_LENGTH
  )
    errors.push(
      `The sample title exceeds the ${EBAY_LISTING_BASE_TITLE_MAX_LENGTH}-character base-title maximum.`,
    );
  if (
    sampleValues !== undefined &&
    renderListingTemplateDescription(descriptionBlocks, sampleValues).length >
      LISTING_DESCRIPTION_MAX_LENGTH
  )
    errors.push(
      `The sample description exceeds the ${LISTING_DESCRIPTION_MAX_LENGTH}-character maximum.`,
    );
  return { valid: errors.length === 0, errors };
}

export type MarketplaceManagementMode = 'EXTERNALLY_MANAGED' | 'TCVS_MANAGED' | 'MIGRATION_PENDING';
export type MarketplaceReconciliationDecision =
  | 'RECONCILED'
  | 'CONFIRMED_EXTERNALLY_MANAGED'
  | 'NOT_ACTIVE_ON_EBAY'
  | 'NOT_IN_TCVS_EXPECTED'
  | 'NEEDS_INVESTIGATION'
  | 'IGNORED';
export type MarketplaceReconciliationSubject =
  | 'matched'
  | 'cdp-only'
  | 'ebay-only'
  | 'external-listing'
  | 'conflicts'
  | 'exception';
export type MarketplaceCommerceState =
  | 'AVAILABLE'
  | 'LISTING_PENDING'
  | 'LISTED'
  | 'SOLD_AWAITING_PICK'
  | 'FULFILLING'
  | 'SOLD'
  | 'RETURN_INSPECTION'
  | 'EXCEPTION';

export interface CdpListingRow {
  rowNumber: number;
  sku: string;
  title?: string;
  sourceBatchId?: string;
}

export interface MarketplaceCardSku {
  cardId: string;
  sku?: string;
  shortCode?: string;
  placementState?: string;
  placementBatchId?: string;
  retiredAt?: string;
}

export interface MarketplaceReconciliationReviewHistoryEntry {
  decision: MarketplaceReconciliationDecision;
  reviewedAt: string;
  reviewedBy?: string;
  note?: string;
}

/** A durable, pair-scoped operator decision. Source import rows remain immutable. */
export interface MarketplaceReconciliationReview {
  comparisonKey: string;
  reviewKey: string;
  decision: MarketplaceReconciliationDecision;
  reviewedAt: string;
  reviewedBy?: string;
  note?: string;
  revision: number;
  history: MarketplaceReconciliationReviewHistoryEntry[];
}

export function marketplaceComparisonKey(cdpImportId: string, ebayImportId: string): string {
  return `CDP#${cdpImportId}|EBAY#${ebayImportId}`;
}

export function marketplaceCardReviewKey(cardId: string): string {
  return `CARD#${cardId}`;
}

export function marketplaceExceptionReviewKey(source: 'CDP' | 'EBAY', rowNumber: number): string {
  return `EXCEPTION#${source}#${String(rowNumber).padStart(6, '0')}`;
}

export function allowedMarketplaceReviewDecisions(
  subject: MarketplaceReconciliationSubject,
): readonly MarketplaceReconciliationDecision[] {
  switch (subject) {
    case 'matched':
      return ['RECONCILED', 'CONFIRMED_EXTERNALLY_MANAGED', 'NEEDS_INVESTIGATION', 'IGNORED'];
    case 'ebay-only':
      return ['CONFIRMED_EXTERNALLY_MANAGED', 'NEEDS_INVESTIGATION', 'IGNORED'];
    case 'external-listing':
      return ['CONFIRMED_EXTERNALLY_MANAGED', 'NEEDS_INVESTIGATION', 'IGNORED'];
    case 'cdp-only':
      return ['NOT_ACTIVE_ON_EBAY', 'NEEDS_INVESTIGATION', 'IGNORED'];
    case 'exception':
      return ['NOT_IN_TCVS_EXPECTED', 'NEEDS_INVESTIGATION', 'IGNORED'];
    case 'conflicts':
      return ['NEEDS_INVESTIGATION', 'IGNORED'];
  }
}

export type CdpMatchMethod = 'full-sku' | 'numeric-prefix' | 'missing' | 'ambiguous';

export interface CdpReconciliationRow {
  rowNumber: number;
  cdpSku: string;
  title?: string;
  sourceBatchId?: string;
  matchMethod: CdpMatchMethod;
  card?: MarketplaceCardSku;
  candidateCardIds?: string[];
}

export interface CdpReconciliationResult {
  rows: CdpReconciliationRow[];
  summary: Record<CdpMatchMethod, number>;
}

export type CdpImportReviewStatus = 'MATCHED' | 'NEEDS_REVIEW';

/** Durable, Dev-only record of one uploaded CDP listing export. */
export interface CdpImportRun {
  importId: string;
  sourceFilename?: string;
  extractedAt?: string;
  importedAt: string;
  importedBy?: string;
  inputRows: number;
  summary: Record<CdpMatchMethod, number>;
  rawObjectKey: string;
}

/** One immutable reconciliation result from a persisted CDP import. */
export interface CdpImportedListingRow extends CdpReconciliationRow {
  reviewStatus: CdpImportReviewStatus;
}

export interface CdpImportDetail {
  run: CdpImportRun;
  rows: CdpImportedListingRow[];
}

export interface EbayActiveListingRow {
  rowNumber: number;
  itemNumber: string;
  customLabel?: string;
  availableQuantity?: number;
  title?: string;
  currentPrice?: string;
  currency?: string;
  condition?: string;
  checklistMatchClaim?: EbayChecklistMatchClaim;
}

/** Exact source identifiers supplied by an offline eBay-to-checklist reconciliation. */
export interface EbayChecklistMatchClaim {
  sourceSystem: 'azure-sql-ebay';
  sourceTargetId: string;
  sourceSubsetId: string;
  sourceItemId: string;
  sourceFamilyId: string;
  confidence: string;
  notes?: string;
  cardNumberHint?: string;
}

export type EbayChecklistMatchValidationStatus =
  | 'VALID'
  | 'ALREADY_APPLIED'
  | 'INVALID'
  | 'CONFLICT';

export interface EbayChecklistMatchValidationRow {
  rowNumber: number;
  itemNumber: string;
  title?: string;
  status: EbayChecklistMatchValidationStatus;
  reason: string;
  comparableKey?: string;
  identity?: CardIdentity;
}

export interface EbayChecklistMatchPageSummary {
  valid: number;
  alreadyApplied: number;
  invalid: number;
  conflict: number;
}

export interface PreviewEbayChecklistMatchesRequest {
  operation: 'PREVIEW_EBAY_CHECKLIST_MATCHES';
  ebayImportId: string;
  startRowNumber?: number;
}

export interface PreviewEbayChecklistMatchesResponse {
  ebayImportId: string;
  startRowNumber: number;
  nextRowNumber?: number;
  totalRows: number;
  pageFingerprint: string;
  summary: EbayChecklistMatchPageSummary;
  rows: EbayChecklistMatchValidationRow[];
}

export interface ApplyEbayChecklistMatchesRequest {
  operation: 'APPLY_EBAY_CHECKLIST_MATCHES';
  ebayImportId: string;
  startRowNumber?: number;
  pageFingerprint: string;
}

export interface ApplyEbayChecklistMatchesResponse {
  ebayImportId: string;
  startRowNumber: number;
  nextRowNumber?: number;
  totalRows: number;
  applied: number;
  unchanged: number;
  invalid: number;
  conflict: number;
}

export type EbayMatchMethod =
  | 'full-sku'
  | 'numeric-prefix'
  | 'missing-custom-label'
  | 'non-unit-quantity'
  | 'missing'
  | 'ambiguous';

/** Current durable checklist linkage projected over an immutable eBay import row. */
export interface EbayChecklistLink {
  marketplaceListingId: string;
  checklistComparablePk: string;
  externalOnly: boolean;
  sourceMatchMethod?: string;
}

export interface EbayReconciliationRow extends EbayActiveListingRow {
  matchMethod: EbayMatchMethod;
  reviewStatus: CdpImportReviewStatus;
  card?: MarketplaceCardSku;
  candidateCardIds?: string[];
  checklistLink?: EbayChecklistLink;
}

export interface EbayImportRun extends Omit<CdpImportRun, 'summary'> {
  provider: 'EBAY_ACTIVE_LISTINGS';
  summary: Record<EbayMatchMethod, number>;
  checklistMatchClaims?: number;
}

export interface EbayImportDetail {
  run: EbayImportRun;
  rows: EbayReconciliationRow[];
}

function parseCsvRecords(text: string): string[][] {
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;
  let started = false;
  const push = (): void => {
    record.push(field);
    field = '';
  };
  const finish = (): void => {
    push();
    records.push(record);
    record = [];
    started = false;
  };
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i] as string;
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (ch === '"') inQuotes = false;
      else field += ch;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      started = true;
    } else if (ch === ',') {
      push();
      started = true;
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      if (started || field !== '') finish();
    } else {
      field += ch;
      started = true;
    }
  }
  if (started || field !== '') finish();
  return records;
}

function columnIndex(header: readonly string[], name: string): number {
  return header.indexOf(name.toLowerCase());
}

/** Parse the bounded subset of a CDP listing export needed for a dry-run match. */
export function parseCdpListingCsv(text: string): CdpListingRow[] {
  const records = parseCsvRecords(text);
  const header =
    records
      .shift()
      ?.map((value, index) =>
        (index === 0 ? value.replace(/^\uFEFF/, '') : value).trim().toLowerCase(),
      ) ?? [];
  const skuIndex = header.indexOf('sku');
  if (skuIndex === -1) throw new Error('cdp_sku_column_required');
  const titleIndex = header.indexOf('title');
  const sectionIndex = header.indexOf('section');
  const rowIndex = header.indexOf('row');
  return records
    .map((cells, index) => ({
      rowNumber: index + 2,
      sku: (cells[skuIndex] ?? '').trim(),
      ...(titleIndex === -1 || (cells[titleIndex] ?? '').trim() === ''
        ? {}
        : { title: (cells[titleIndex] ?? '').trim() }),
      ...(sectionIndex !== -1 && (cells[sectionIndex] ?? '').trim() !== ''
        ? { sourceBatchId: (cells[sectionIndex] ?? '').trim() }
        : rowIndex !== -1 && (cells[rowIndex] ?? '').trim() !== ''
          ? { sourceBatchId: (cells[rowIndex] ?? '').trim() }
          : {}),
    }))
    .filter((row) => row.sku !== '');
}

/** Parse eBay's Active Listings report without treating its values as provider API input. */
export function parseEbayActiveListingsCsv(text: string): EbayActiveListingRow[] {
  const records = parseCsvRecords(text);
  const header = records.shift()?.map((value) => value.trim().toLowerCase()) ?? [];
  const itemNumberIndex = columnIndex(header, 'item number');
  const customLabelIndex = columnIndex(header, 'custom label (sku)');
  if (itemNumberIndex === -1 || customLabelIndex === -1)
    throw new Error('ebay_active_listing_columns_required');
  const quantityIndex = columnIndex(header, 'available quantity');
  const titleIndex = columnIndex(header, 'title');
  const priceIndex = columnIndex(header, 'current price');
  const currencyIndex = columnIndex(header, 'currency');
  const conditionIndex = columnIndex(header, 'condition');
  const enrichedColumns = {
    sourceTargetId: columnIndex(header, 'match_target_id'),
    sourceSubsetId: columnIndex(header, 'match_subset_id'),
    sourceItemId: columnIndex(header, 'match_item_id'),
    sourceFamilyId: columnIndex(header, 'match_family_id'),
    confidence: columnIndex(header, 'match_confidence'),
    notes: columnIndex(header, 'match_notes'),
  };
  const enrichedColumnCount = Object.values(enrichedColumns).filter((index) => index !== -1).length;
  if (enrichedColumnCount !== 0 && enrichedColumnCount !== Object.keys(enrichedColumns).length)
    throw new Error('ebay_checklist_match_columns_incomplete');
  return records.map((cells, index) => {
    const textAt = (column: number): string | undefined => {
      const value = (cells[column] ?? '').trim();
      return value === '' ? undefined : value;
    };
    const quantityText = quantityIndex === -1 ? undefined : textAt(quantityIndex);
    const quantity = quantityText === undefined ? undefined : Number(quantityText);
    const matchNotes = enrichedColumns.notes === -1 ? undefined : textAt(enrichedColumns.notes);
    const cardNumberHint =
      matchNotes === undefined
        ? undefined
        : /^matched card_number '([^']+)'(?:,|$)/.exec(matchNotes)?.[1]?.trim();
    const checklistMatchClaim: EbayChecklistMatchClaim | undefined =
      enrichedColumnCount === 0
        ? undefined
        : {
            sourceSystem: 'azure-sql-ebay',
            sourceTargetId: textAt(enrichedColumns.sourceTargetId) ?? '',
            sourceSubsetId: textAt(enrichedColumns.sourceSubsetId) ?? '',
            sourceItemId: textAt(enrichedColumns.sourceItemId) ?? '',
            sourceFamilyId: textAt(enrichedColumns.sourceFamilyId) ?? '',
            confidence: (textAt(enrichedColumns.confidence) ?? '').toLowerCase(),
            ...(matchNotes === undefined ? {} : { notes: matchNotes }),
            ...(cardNumberHint === undefined || cardNumberHint === '' ? {} : { cardNumberHint }),
          };
    return {
      rowNumber: index + 2,
      itemNumber: textAt(itemNumberIndex) ?? '',
      ...(textAt(customLabelIndex) === undefined ? {} : { customLabel: textAt(customLabelIndex) }),
      ...(Number.isInteger(quantity) && quantity !== undefined
        ? { availableQuantity: quantity }
        : {}),
      ...(titleIndex === -1 || textAt(titleIndex) === undefined
        ? {}
        : { title: textAt(titleIndex) }),
      ...(priceIndex === -1 || textAt(priceIndex) === undefined
        ? {}
        : { currentPrice: textAt(priceIndex) }),
      ...(currencyIndex === -1 || textAt(currencyIndex) === undefined
        ? {}
        : { currency: textAt(currencyIndex) }),
      ...(conditionIndex === -1 || textAt(conditionIndex) === undefined
        ? {}
        : { condition: textAt(conditionIndex) }),
      ...(checklistMatchClaim === undefined ? {} : { checklistMatchClaim }),
    };
  });
}

export function cdpNumericSkuPrefix(sku: string): string | undefined {
  const match = /^(\d+)-.+$/.exec(sku.trim());
  return match?.[1];
}

/**
 * Full CDP SKU equality is preferred. Legacy TCVS records that retain only the
 * numeric CDP prefix are accepted only when that produces one candidate.
 */
export function reconcileCdpListingRows(
  rows: readonly CdpListingRow[],
  cards: readonly MarketplaceCardSku[],
): CdpReconciliationResult {
  const bySku = new Map<string, MarketplaceCardSku[]>();
  for (const card of cards) {
    const sku = card.sku?.trim();
    if (sku === undefined || sku === '') continue;
    const existing = bySku.get(sku) ?? [];
    existing.push(card);
    bySku.set(sku, existing);
  }
  const summary: Record<CdpMatchMethod, number> = {
    'full-sku': 0,
    'numeric-prefix': 0,
    missing: 0,
    ambiguous: 0,
  };
  const resultRows = rows.map((row) => {
    const full = bySku.get(row.sku) ?? [];
    const prefix = cdpNumericSkuPrefix(row.sku);
    const legacy = prefix === undefined ? [] : (bySku.get(prefix) ?? []);
    const candidates = full.length > 0 ? full : legacy;
    const matchMethod: CdpMatchMethod =
      candidates.length === 1
        ? full.length === 1
          ? 'full-sku'
          : 'numeric-prefix'
        : candidates.length === 0
          ? 'missing'
          : 'ambiguous';
    summary[matchMethod] += 1;
    return {
      rowNumber: row.rowNumber,
      cdpSku: row.sku,
      ...(row.title === undefined ? {} : { title: row.title }),
      ...(row.sourceBatchId === undefined ? {} : { sourceBatchId: row.sourceBatchId }),
      matchMethod,
      ...(candidates.length === 1 ? { card: candidates[0] as MarketplaceCardSku } : {}),
      ...(candidates.length > 1 ? { candidateCardIds: candidates.map((card) => card.cardId) } : {}),
    };
  });
  return { rows: resultRows, summary };
}

/** Reconcile eBay custom labels while retaining listing exceptions for operator review. */
export function reconcileEbayActiveListingRows(
  rows: readonly EbayActiveListingRow[],
  cards: readonly MarketplaceCardSku[],
): { rows: EbayReconciliationRow[]; summary: Record<EbayMatchMethod, number> } {
  const bySku = new Map<string, MarketplaceCardSku[]>();
  for (const card of cards) {
    const sku = card.sku?.trim();
    if (sku === undefined || sku === '') continue;
    bySku.set(sku, [...(bySku.get(sku) ?? []), card]);
  }
  const summary: Record<EbayMatchMethod, number> = {
    'full-sku': 0,
    'numeric-prefix': 0,
    'missing-custom-label': 0,
    'non-unit-quantity': 0,
    missing: 0,
    ambiguous: 0,
  };
  const reconciled = rows.map((row) => {
    if (row.customLabel === undefined)
      return { ...row, matchMethod: 'missing-custom-label' as const };
    if (row.availableQuantity !== 1) return { ...row, matchMethod: 'non-unit-quantity' as const };
    const full = bySku.get(row.customLabel) ?? [];
    const prefix = cdpNumericSkuPrefix(row.customLabel);
    const candidates =
      full.length > 0 ? full : prefix === undefined ? [] : (bySku.get(prefix) ?? []);
    const matchMethod: EbayMatchMethod =
      candidates.length === 1
        ? full.length === 1
          ? 'full-sku'
          : 'numeric-prefix'
        : candidates.length === 0
          ? 'missing'
          : 'ambiguous';
    return {
      ...row,
      matchMethod,
      ...(candidates.length === 1 ? { card: candidates[0] } : {}),
      ...(candidates.length > 1 ? { candidateCardIds: candidates.map((card) => card.cardId) } : {}),
    };
  });
  return {
    rows: reconciled.map((row) => {
      summary[row.matchMethod] += 1;
      return {
        ...row,
        reviewStatus:
          row.matchMethod === 'full-sku' || row.matchMethod === 'numeric-prefix'
            ? 'MATCHED'
            : 'NEEDS_REVIEW',
      };
    }),
    summary,
  };
}
